import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, CornerDownLeft, History, Search, X } from 'lucide-react'
import { buildIndex, searchIndex } from './uiIndex'
import { shell } from './strings'
import './MenuSearch.css'

/** How many results the popover shows, and how many recents it remembers. */
const LIMIT = 9
const RECENTS = 5
const RECENT_KEY = 'rawam.recentPlaces'

const readRecent = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]')
    return Array.isArray(stored) ? stored.slice(0, RECENTS) : []
  } catch {
    /* A hand-edited or half-written entry is not worth a broken header. */
    return []
  }
}

const GROUP_LABEL = {
  page: shell.searchPages,
  section: shell.searchSections,
  action: shell.searchActions,
}

/**
 * Jump straight to any destination in the panel by name — a page, a panel
 * inside one, or the screen an action opens.
 *
 * It began as a filter inside the rail, then became a field in the command bar
 * that searched the ~40 rail entries. Both had the same limit: the rail is a
 * list of PAGES, and most of what an admin goes hunting for is not one. The
 * eight panels of /settings, the seven of /settings/advanced, the three
 * gamification tabs — all of them are things you can only reach by knowing which
 * page owns them. Now they are all in one index (uiIndex.js) with the trail that
 * says where each one lives, and a hit takes you to the part, not just the page.
 *
 * Every result prints its full path under its title, because half the index
 * would otherwise be ambiguous: three different panels are called `الإعدادات`,
 * and the only thing telling them apart is the group above them.
 *
 * Empty and focused, it offers where you have been. A search box you have to
 * type into before it says anything is no help at all to the reader who cannot
 * remember what the thing was called — which is the reader this exists for.
 */
export default function MenuSearch({ session }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [recent, setRecent] = useState(readRecent)
  const rootRef = useRef(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  /* ~90 entries of static text, folded for matching once per session rather
     than once per keystroke. */
  const index = useMemo(() => buildIndex(session), [session])

  const needle = query.trim()
  const hits = useMemo(() => searchIndex(index, needle, LIMIT), [index, needle])

  /* Recents are stored as ids, not as entries: an id survives a rename, a
     re-order and a licence change, and anything no longer in the index — a page
     this session may not open — simply drops out. */
  const history = useMemo(
    () => recent.map((id) => index.find((item) => item.id === id)).filter(Boolean),
    [index, recent],
  )

  const rows = needle ? hits : history
  const showing = open && (needle.length > 0 || rows.length > 0)

  // Reset the highlight whenever the result set changes, or Enter would open
  // whatever happened to sit at the old index.
  useEffect(() => setActive(0), [needle, showing])

  // Keep the highlighted row in view when the arrows walk past the fold.
  useEffect(() => {
    listRef.current
      ?.querySelector('.menu-search__hit.is-active')
      ?.scrollIntoView({ block: 'nearest' })
  }, [active])

  // Ctrl/Cmd+K focuses the field from anywhere.
  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  // A click anywhere else puts the results away.
  useEffect(() => {
    if (!showing) return undefined
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [showing])

  const go = (hit) => {
    const next = [hit.id, ...recent.filter((id) => id !== hit.id)].slice(0, RECENTS)
    setRecent(next)
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next))
    } catch {
      /* Private mode, or a full quota — the jump still works. */
    }
    setQuery('')
    setOpen(false)
    inputRef.current?.blur()
    navigate(hit.to)
  }

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (query) setQuery('')
      else {
        setOpen(false)
        inputRef.current?.blur()
      }
      return
    }
    if (!showing || !rows.length) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((current) => (current + 1) % rows.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((current) => (current - 1 + rows.length) % rows.length)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      go(rows[active])
    }
  }

  return (
    <div className="menu-search" ref={rootRef}>
      <Search className="menu-search__icon" size={15} strokeWidth={2} aria-hidden="true" />
      <input
        ref={inputRef}
        className="menu-search__input"
        type="text"
        role="combobox"
        aria-expanded={showing}
        aria-controls="menu-search-results"
        aria-autocomplete="list"
        aria-activedescendant={showing && rows[active] ? `menu-search-hit-${active}` : undefined}
        value={query}
        placeholder={shell.search}
        aria-label={shell.search}
        onChange={(event) => {
          setQuery(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {query ? (
        <button
          type="button"
          className="menu-search__clear"
          onClick={() => {
            setQuery('')
            inputRef.current?.focus()
          }}
          aria-label={shell.searchClear}
        >
          <X size={13} strokeWidth={2.4} />
        </button>
      ) : (
        <span className="menu-search__kbd" aria-hidden="true">
          Ctrl K
        </span>
      )}

      {showing && (
        <div className="menu-search__results" id="menu-search-results" ref={listRef}>
          {rows.length === 0 ? (
            <p className="menu-search__empty">
              <bdi>{shell.searchEmpty}</bdi>
            </p>
          ) : (
            <ul className="menu-search__list" role="listbox" aria-label={shell.search}>
              {rows.map((hit, position) => {
                const Icon = hit.icon
                /* A heading before the first result of each kind. With no query
                   there is one heading — "آخر ما فتحت" — over a list that mixes
                   kinds, because what groups those is when you opened them. */
                const heading = !needle
                  ? position === 0 && shell.searchRecent
                  : hit.kind !== rows[position - 1]?.kind && GROUP_LABEL[hit.kind]

                return (
                  /* `presentation`, so the option below is a direct child of the
                     listbox as far as a screen reader is concerned — a bare <li>
                     between the two is an unexpected node in that role. */
                  <li key={hit.id} role="presentation">
                    {heading && (
                      <p className="menu-search__heading">
                        {!needle && (
                          <History size={12} strokeWidth={2} aria-hidden="true" />
                        )}
                        <bdi>{heading}</bdi>
                      </p>
                    )}

                    <button
                      type="button"
                      id={`menu-search-hit-${position}`}
                      role="option"
                      aria-selected={position === active}
                      className={`menu-search__hit${position === active ? ' is-active' : ''}`}
                      onMouseMove={() => setActive(position)}
                      onClick={() => go(hit)}
                    >
                      <span className="menu-search__hit-icon" aria-hidden="true">
                        {Icon && <Icon size={16} strokeWidth={1.75} />}
                      </span>

                      <span className="menu-search__hit-text">
                        <span className="menu-search__hit-title">
                          <bdi>
                            <Marked text={hit.title} range={hit.marks} />
                          </bdi>
                        </span>
                        <span className="menu-search__hit-trail">
                          <Trail parts={hit.trail} />
                        </span>
                      </span>

                      <CornerDownLeft
                        className="menu-search__hit-enter ltr-flip"
                        size={13}
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          <p className="menu-search__legend" aria-hidden="true">
            <span>
              <kbd>↑</kbd>
              <kbd>↓</kbd> {shell.searchMove}
            </span>
            <span>
              <kbd>↵</kbd> {shell.searchOpen}
            </span>
            <span>
              <kbd>esc</kbd> {shell.searchClose}
            </span>
          </p>
        </div>
      )}
    </div>
  )
}

/** The matched run of the title, marked. `range` is `[start, end)` on the original. */
function Marked({ text, range }) {
  if (!range) return text
  const [start, end] = range
  return (
    <>
      {text.slice(0, start)}
      <mark className="menu-search__mark">{text.slice(start, end)}</mark>
      {text.slice(end)}
    </>
  )
}

/**
 * Where the destination lives — everything ABOVE it: `الإعدادات › إعدادات عامة`
 * under a title of `إعدادات التنبيهات`.
 *
 * It carried the leaf too for a while, on the theory that a path should end at
 * the thing it describes. On screen that put the same three words on both lines
 * of every section result, and the eye reads them twice before finding the one
 * word that differs. The title IS the leaf.
 *
 * The chevron points onward, which is leftward in Arabic — so it is `.ltr-flip`
 * (drawn for Arabic, mirrored in English), the opposite class from an arrow
 * drawn for English.
 */
function Trail({ parts }) {
  return parts.map((step, index) => (
    <span className="menu-search__crumb" key={`${step}-${index}`}>
      {index > 0 && (
        <ChevronLeft
          className="menu-search__sep ltr-flip"
          size={11}
          strokeWidth={2.4}
          aria-hidden="true"
        />
      )}
      <bdi>{step}</bdi>
    </span>
  ))
}
